
import {
  JwtStore,
  CommonStore,
  FarmStore,
  ConfigurationStore,
  BlockStore,
  FloweringStore,
  MapStore
} from '@/store/modules';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { RawLocation, Route } from 'vue-router';

import variables from '@/styles/exportedVariables.sass';

import { roleChecker } from '@/utils/jwt';

import SvgIcon from '@/components/SvgIcon.vue';
import PasswordChangeDialog from '@/components/Dialogs/PasswordChangeDialog.vue';
import { UserTypes } from '@/store/models/user';
import { Farm } from '@/store/models/farm';
import { Coordinate } from 'ol/coordinate';
import { SelectedFarmBlock } from '@/store/models/common';
import helpers from '@/utils/helpers';
import { Extent } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { Season } from '@/store/models/configuration';

import { date } from 'quasar';

@Component({
  name: 'MainLayout',
  meta: {
    title: 'FruitMaps'
  },
  components: {
    // RoutesList
    SvgIcon,
    PasswordChangeDialog
  }
})
export default class MainLayout extends Vue {
  $refs!: {
    farmFilterHeader: InstanceType<typeof HTMLElement>;
    farmFilterPage: InstanceType<typeof Vue>;
  };
  changePasswordDialog = false;
  sensorIcon = require('@/assets/temperature.svg');
  tabs = '';
  currentFarmId = 0;
  previousFarm: SelectedFarmBlock | undefined = undefined;

  get UserTypes() {
    return UserTypes;
  }
  get isLoggedIn() {
    return JwtStore.isLoggedIn;
  }

  get userDetail() {
    return JwtStore.UserDetail;
  }

  get sidePanelWidth() {
    const width = Number(variables.sidePanelWidth.replace('px', ''));
    return width;
  }

  get sidePanelMiniWidth() {
    const width = Number(variables.sidePanelMiniWidth.replace('px', ''));
    return width;
  }
  get headerFooterHeight() {
    const height = Number(variables.headerFooterHeight.replace('px', ''));
    return height;
  }
  get view() {
    return CommonStore.View;
  }
  set view(view: string) {
    CommonStore.changeView(view);
  }

  get selectedFarmBlock() {
    const selectedFarmBlock = CommonStore.SelectedFarmBlock;
    return selectedFarmBlock;
  }

  set selectedFarmBlock(selectedFarmBlock) {
    CommonStore.setSelectedFarmBlock(selectedFarmBlock);
  }

  @Watch('selectedFarmBlock', { deep: true, immediate: true })
  async onSelectedFarmBlockChange(newVal: SelectedFarmBlock) {
    await helpers.sleep(1);
    this.currentFarmId = newVal.farmId;
    this.selectedFarmId = newVal.farmId;
    if (this.currentFarmId && this.currentFarmId != 0) {
      await ConfigurationStore.getLatestSeasonDateByFarm(this.currentFarmId);
    }
  }

  @Watch('$route', { deep: true })
  async onPathChange(route: Route, oldRoute: Route) {
    if (oldRoute.name == 'FarmYieldByWeek') {
      const previousFarm = this.$q.localStorage.getItem(
        'SelectedFarmBlock'
      ) as SelectedFarmBlock | null;
      if (previousFarm) {
        this.selectedFarmBlock = previousFarm;
      } else {
        FarmStore.getMyDefaultFarm();
      }
    }
    await helpers.sleep(1);
    this.resizePage();
  }

  get farmList() {
    const farms = JSON.parse(JSON.stringify(FarmStore.Farms)) as Farm[];
    if (this.$route.name == 'FarmYieldByWeek') {
      farms.unshift({
        id: 0,
        name: 'All',
        managerList: [],
        farmManager: [],
        area: 0,
        polygonGeom: '',
        pointGeom: ''
      });
    }
    return farms;
  }

  selectedFarmId = 0;

  selectFarmId(id: number) {
    const farm = this.farmList.find(x => x.id == id);
    if (farm) {
      this.selectFarm(farm);
    }
  }

  selectFarm(farm: Farm) {
    if (typeof farm.pointGeom != 'string') {
      const coordinate = farm.pointGeom.coordinates as Coordinate;
      let extent: Extent | null = null;
      if (farm.envelope) {
        const geometry = new GeoJSON({
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        }).readGeometry(farm.envelope);
        extent = geometry.getExtent();
      }
      this.selectedFarmBlock = {
        farmId: farm.id,
        name: farm.name,
        center: coordinate,
        extent: extent
      };
    } else {
      this.selectedFarmBlock = {
        farmId: farm.id,
        name: farm.name,
        center: [0, 0],
        extent: null
      };
    }
    this.loadSeasonAndCrop();
  }

  roleChecker(roles: UserTypes[]) {
    return roleChecker(roles);
  }

  gotoRoute(route: RawLocation, tab: null | string = null) {
    if (tab) {
      this.tabs = tab;
    } else {
      this.tabs = '';
    }
    this.$router.push(route);
  }

  changeView(view: string) {
    if (this.$route.name != 'Home') {
      this.$router.push({ name: 'Home' });
    }
    this.view = view;
  }

  get showDarkModeBtn() {
    if (process.env.VUE_APP_DARK_MODE) {
      return true;
    }
    return false;
  }
  toggleDarkMode() {
    if (this.$q.dark.isActive) {
      this.$q.dark.set(false);
    } else {
      this.$q.dark.set(true);
    }
  }

  async clearJWT() {
    await JwtStore.clearJWT();
    this.$router.push({ name: 'LandingPage' });
    this.$q.notify({
      message: 'You logged out successfully',
      type: 'warning'
    });
  }

  async loadSeasonAndCrop() {
    this.cropId = 0;
    this.currentSeasonId = null;
    await helpers.fetchingDefaultFarm();

    await BlockStore.getFarmAllCrops({ farmId: this.selectedFarmBlock.farmId });
    this.seasonLoading = true;
    await ConfigurationStore.getSeasonList({
      farm: this.selectedFarmBlock.farmId
    });
    this.seasonLoading = false;

    this.resetDate();
  }

  async created() {
    this.selectedFarmId = this.selectedFarmBlock.farmId;
    FarmStore.getAllMyFarms();
    this.loadSeasonAndCrop();

    MapStore.setLoadingBaseLayers(true);
    await MapStore.getBaseLayers();
    this.onDateChange();
  }

  resizePage() {
    try {
      const farmFilterHeaderHeight = this.$refs.farmFilterHeader.clientHeight;
      const farmFilterPageElement = this.$refs.farmFilterPage
        .$el as HTMLElement;
      farmFilterPageElement.style.marginTop = `${farmFilterHeaderHeight}px`;
      farmFilterPageElement.style.minHeight = 'unset';
      const mapView = document.getElementById('mapView');
      if (mapView) {
        mapView.style.height = `calc(100% - ${this.headerFooterHeight}px - ${this.headerFooterHeight}px - ${farmFilterHeaderHeight}px)`;
      }
      const leftMap = document.getElementById('left-map');
      if (leftMap) {
        leftMap.style.height = `calc(100% - ${this.headerFooterHeight}px - ${farmFilterHeaderHeight}px - 1px)`;
      }
      const rightMap = document.getElementById('right-map');
      if (rightMap) {
        rightMap.style.height = `calc(100% - ${this.headerFooterHeight}px - ${farmFilterHeaderHeight}px - 1px)`;
      }
      const tableView = document.getElementById('tableView');
      if (tableView) {
        tableView.style.height = `calc(100% - ${this.headerFooterHeight}px - ${this.headerFooterHeight}px - ${farmFilterHeaderHeight}px)`;
      }
    } catch (error) {
      // pass
    }
  }

  async mounted() {
    window.addEventListener('resize', this.resizePage);
    this.resizePage();
    await helpers.sleep(500);
    this.resizePage();
  }

  async resetDate() {
    // const days = 365;
    // const today = new Date();
    // const last = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
    // this.startDate = last.toJSON().split('T')[0];
    // this.endDate = today.toJSON().split('T')[0];

    const today = new Date();
    const currentYear = today.getFullYear();
    let startDate = `${currentYear - 1}-04-01`;
    let endDate = `${currentYear}-03-31`;
    if (!date.isBetweenDates(today, startDate, endDate)) {
      startDate = `${currentYear}-04-01`;
      endDate = `${currentYear + 1}-03-31`;
    }
    this.startDate = startDate;
    this.endDate = endDate;

    if (this.currentFarmId && this.currentFarmId != 0) {
      await ConfigurationStore.getLatestSeasonDateByFarm(this.currentFarmId);
      const latestDate = ConfigurationStore.LatestSeasonDate;
      this.startDate = latestDate.startDate;
      this.endDate = latestDate.endDate;
      // Force update date change if latest season date is updated
      this.dateChangeQueue = 0;
    }
  }
  startDateVisible = false;
  endDateVisible = false;
  seasonLoading = false;

  nullSeason = {
    id: null,
    farm: 0,
    crop: 0,
    cropVariety: 0,
    name: 'All',
    startDate: '',
    endDate: '',
    value: null,
    label: 'All'
  };

  get crops() {
    const crops = BlockStore.SeasonCrops.map(x => {
      return {
        value: x.id,
        variety: x.variety,
        label: `${x.cropTypeName} - ${x.varietyName}`
      };
    });
    crops.unshift({
      value: 0,
      variety: 0,
      label: `All`
    });
    return crops;
  }

  get groupedCrops() {
    const crops = BlockStore.SeasonCrops.map(x => {
      return {
        value: x.id,
        variety: x.variety,
        label: `${x.cropTypeName} - ${x.varietyName}`,
        crop: x.cropTypeName,
        type: 'option'
      };
    });
    crops.unshift({
      value: 0,
      variety: 0,
      crop: '',
      label: `All`,
      type: 'option'
    });
    const groups = crops.reduce((groups: { [key: string]: any }, item) => {
      const group = groups[item.crop] || [];
      group.push(item);
      groups[item.crop] = group;
      return groups;
    }, {});
    const groupedCrops = [];
    for (const groupName in groups) {
      if (groupName != '') {
        groupedCrops.push({
          value: 0,
          variety: 0,
          crop: '',
          label: groupName,
          type: 'group'
        });
      }
      groupedCrops.push(...groups[groupName]);
    }
    return groupedCrops;
  }

  get cropId() {
    const cropId = CommonStore.CurrentCrop;
    return cropId;
  }

  set cropId(value) {
    CommonStore.setCurrentCrop(value);
  }

  get seasons() {
    let seasons = ConfigurationStore.Seasons.map(x => {
      return { value: x.id, label: x.name, ...x };
    });

    if (this.cropId == 0) {
      seasons = [this.nullSeason];
      this.currentSeasonId = null;
      this.resetDate();
    } else {
      seasons = seasons.filter(x => x.crop == this.cropId);
      if (seasons.length > 0) {
        this.currentSeason = seasons[0];
      } else {
        this.currentSeasonId = null;
        this.resetDate();
      }
    }
    return seasons;
  }

  get currentSeason() {
    return CommonStore.CurrentSeason;
  }
  set currentSeason(season: Season | null) {
    if (season && season.startDate != '') {
      this.startDate = date.formatDate(season.startDate, 'YYYY-MM-DD');
      this.endDate = date.formatDate(season.endDate, 'YYYY-MM-DD');
    } else {
      this.resetDate();
    }

    CommonStore.setCurrentSeason(season);
  }
  get currentSeasonId() {
    return this.currentSeason?.id;
  }
  set currentSeasonId(seasonId: number | null | undefined) {
    if (seasonId == null) {
      this.currentSeason = JSON.parse(JSON.stringify(this.nullSeason));
      this.resetDate();
    } else {
      const currentSeason = this.seasons.find(x => x.value == seasonId);
      if (currentSeason) {
        this.currentSeason = currentSeason;
      } else {
        this.currentSeason = null;
        this.resetDate();
      }
    }
  }

  get startDate() {
    return CommonStore.StartDate;
  }

  set startDate(date: string) {
    CommonStore.setStartDate(date);
    this.startDateVisible = false;
  }

  get endDate() {
    return CommonStore.EndDate;
  }

  set endDate(date: string) {
    CommonStore.setEndDate(date);
    this.endDateVisible = false;
  }

  allowedStartDateOption(date: string) {
    if (this.currentSeason == null || this.currentSeason.value == null) {
      if (this.endDate) {
        return date <= this.endDate.replaceAll('-', '/');
      }
      return true;
    }
    return (
      date <= this.endDate.replaceAll('-', '/') &&
      date >= this.currentSeason.startDate.replaceAll('-', '/')
    );
  }

  allowedEndDateOption(date: string) {
    if (this.currentSeason == null || this.currentSeason.value == null) {
      if (this.startDate) {
        return date >= this.startDate.replaceAll('-', '/');
      }
      return true;
    }
    return (
      date >= this.startDate.replaceAll('-', '/') &&
      date <= this.currentSeason.endDate.replaceAll('-', '/')
    );
  }

  @Watch('startDate')
  onStartDateChange() {
    this.onDateChange();
  }

  @Watch('endDate')
  onEndDateChange() {
    this.onDateChange();
  }

  dateChangeQueue = 0;

  dateChange() {
    FloweringStore.setFloweringStartDate(this.startDate);
    FloweringStore.setFloweringEndDate(this.endDate);
    FloweringStore.setFloweringFirstDate(this.startDate);
    FloweringStore.setFloweringLastDate(this.endDate);

    this.dateChangeQueue++;
    FloweringStore.getFloweringsDates({
      dateStart: this.startDate,
      dateEnd: this.endDate,
      farm: this.selectedFarmBlock.farmId
    }).then(() => {
      this.dateChangeQueue--;
    });

    this.dateChangeQueue++;
    FloweringStore.getFruitCountDates({
      dateStart: this.startDate,
      dateEnd: this.endDate,
      farm: this.selectedFarmBlock.farmId,
      block: this.selectedFarmBlock.blockId
    }).then(() => {
      this.dateChangeQueue--;
    });

    this.dateChangeQueue++;
    FloweringStore.getFruitSizeDates({
      dateStart: this.startDate,
      dateEnd: this.endDate,
      farm: this.selectedFarmBlock.farmId,
      block: this.selectedFarmBlock.blockId
    }).then(() => {
      this.dateChangeQueue--;
    });

    this.dateChangeQueue++;
    FloweringStore.getDryMatterDates({
      dateStart: this.startDate,
      dateEnd: this.endDate,
      farm: this.selectedFarmBlock.farmId,
      block: this.selectedFarmBlock.blockId
    }).then(() => {
      this.dateChangeQueue--;
    });
  }

  async onDateChange() {
    if (this.dateChangeQueue <= 0) {
      // We need to delay few milliseconds so both start & end date gets updated in vuex
      await helpers.sleep(100);
      this.dateChange();
    }
  }
}
